// Реактивные SQL-запросы через PowerSync. usePowerSyncQuery автоматически
// перерендерит компонент при любом изменении в синкаемых таблицах.
//
// Преимущество над Dexie: SQL joins, агрегации, ORDER BY работают
// натурально. Тип возврата выводится из generic-параметра.
//
// Local*-типы здесь — РЕАЛЬНЫЕ колонки БД (information_schema), как и в
// `lib/powersync/queries/`. Канонические хуки чтения — в `lib/powersync/queries`;
// этот файл оставлен для совместимости с уже подключёнными потребителями
// (`routes/materials/$materialId/notes.tsx` → useLocalNotesForMaterial).
//
// Полная документация хуков: https://docs.powersync.com/client-sdk-references/javascript-web/javascript-web-orm
import { usePowerSyncQuery } from "@/lib/powersync/use-query";

export interface LocalMaterial {
	id: string;
	user_id: string;
	name: string;
	type: string;
	processing_status: string;
	mime_type: string | null;
	file_size_bytes: number | null;
	language: string | null;
	created_at: string;
	updated_at: string;
}

export function useLocalMaterials(userId: string | null) {
	return usePowerSyncQuery<LocalMaterial>(
		`SELECT * FROM materials
       WHERE user_id = ? AND deleted_at IS NULL
       ORDER BY updated_at DESC`,
		[userId ?? ""],
	);
}

export function useLocalMaterial(materialId: string | null) {
	return usePowerSyncQuery<LocalMaterial>(`SELECT * FROM materials WHERE id = ? LIMIT 1`, [
		materialId ?? "",
	]);
}

export interface LocalNote {
	id: string;
	user_id: string;
	material_id: string | null;
	title: string;
	content_markdown: string | null;
	version: number;
	updated_at: string;
	/** 'generating' | 'ready' | 'failed' | null (legacy = ready). Placeholder = 'generating'. */
	generation_status: string | null;
}

export function useLocalNotesForMaterial(materialId: string | null) {
	return usePowerSyncQuery<LocalNote>(
		`SELECT * FROM notes
       WHERE material_id = ? AND deleted_at IS NULL
       ORDER BY version DESC`,
		[materialId ?? ""],
	);
}

export interface DueFlashcard {
	id: string;
	set_id: string;
	front: string;
	back: string;
	srs_due_at: string | null;
}

export function useDueFlashcards(userId: string | null) {
	// flashcards.user_id присутствует в реальной схеме; JOIN на сеты — для
	// фильтрации soft-deleted наборов. SRS due-поле — srs_due_at.
	return usePowerSyncQuery<DueFlashcard>(
		`SELECT f.id, f.set_id, f.front, f.back, f.srs_due_at
       FROM flashcards f
       INNER JOIN flashcard_sets s ON f.set_id = s.id
       WHERE s.user_id = ?
         AND s.deleted_at IS NULL
         AND f.deleted_at IS NULL
         AND (f.srs_due_at IS NULL OR f.srs_due_at <= datetime('now'))
       ORDER BY f.srs_due_at ASC NULLS FIRST
       LIMIT 50`,
		[userId ?? ""],
	);
}

export interface LocalTutorChat {
	id: string;
	user_id: string;
	title: string | null;
	chat_mode: string;
	updated_at: string;
}

export function useLocalTutorChats(userId: string | null) {
	// Таблица переименована в tutor_sessions (Phase 0); поле режима — chat_mode.
	return usePowerSyncQuery<LocalTutorChat>(
		`SELECT * FROM tutor_sessions
       WHERE user_id = ? AND deleted_at IS NULL
       ORDER BY updated_at DESC
       LIMIT 100`,
		[userId ?? ""],
	);
}
